/**
 * ContactModal — bare-minimum contact form for the landing footer.
 *
 * Three fields (name optional, email required, message required), POSTs
 * to /api/v1/contact, which sends the submission to support@mindfrontier.io
 * via CloudMailin with the visitor's email as Reply-To.
 *
 * Modal closes on Esc, backdrop click, or "×" button. On success, the
 * form is replaced by a confirmation; the user can close manually.
 */

import React, { useEffect, useRef, useState } from 'react';

const API_BASE = import.meta.env.REACT_APP_API_URL || window.location.origin;

export default function ContactModal({ open, onClose }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState({ kind: 'idle', text: '' });
  const firstFieldRef = useRef(null);
  const overlayRef = useRef(null);

  // Reset whenever the modal re-opens so a stale success message doesn't
  // greet a returning user.
  useEffect(() => {
    if (open) {
      setStatus({ kind: 'idle', text: '' });
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    }
  }, [open]);

  // Esc closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !message) return;
    setStatus({ kind: 'loading', text: 'sending…' });
    try {
      const res = await fetch(`${API_BASE}/api/v1/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message }),
      });
      if (res.ok) {
        setStatus({ kind: 'success', text: '' });
        setName(''); setEmail(''); setMessage('');
        return;
      }
      // 429 rate-limit, 422 validation, 502/503 mailer issues, etc.
      let detail = 'Something went wrong. Please try again or email us directly.';
      try {
        const body = await res.json();
        if (body?.detail && typeof body.detail === 'string') detail = body.detail;
      } catch {/* ignore parse error */}
      setStatus({ kind: 'error', text: detail });
    } catch (err) {
      setStatus({ kind: 'error', text: 'Network error. Please check your connection and try again.' });
    }
  };

  const onBackdrop = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const isSuccess = status.kind === 'success';
  const isLoading = status.kind === 'loading';

  return (
    <div className="landing-contact-overlay" ref={overlayRef} onClick={onBackdrop} role="dialog" aria-modal="true" aria-labelledby="contact-title">
      <div className="landing-contact-modal">
        <button type="button" className="landing-contact-close" onClick={onClose} aria-label="Close">×</button>
        <div className="lbl">Contact us</div>
        <h3 id="contact-title">Drop us a note.</h3>
        <p className="sub">We read every message. Expect a reply within one business day, Sydney time.</p>

        {isSuccess ? (
          <div className="success">
            <strong>Thanks, your message is on its way.</strong><br />
            We'll get back to you at the email you provided. If it's urgent, you can also write to
            {' '}<a href="mailto:support@mindfrontier.io" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>support@mindfrontier.io</a>.
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <label>
              Name <span style={{ color: 'var(--ink-faint)', textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
              <input ref={firstFieldRef} type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" />
            </label>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={320} autoComplete="email" />
            </label>
            <label>
              Message
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} required maxLength={4000} rows={5} />
            </label>
            <div className="actions">
              <span className={'status' + (status.kind === 'error' ? ' error' : '')}>{status.text}</span>
              <button type="submit" className="submit" disabled={isLoading || !email || !message}>
                {isLoading ? 'Sending…' : 'Send'}
                {!isLoading && <span aria-hidden="true">→</span>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
